/**
 * What a product is claimed to do, or what an experiment found -- and why each process was chosen.
 *
 * ONE COMPONENT FOR BOTH SPECIES, because the page has the same three parts either way: the
 * record's own fields, the design decisions behind it, and the reason attached to each. What
 * differs is which fields, and that is a table in this file rather than two components that would
 * drift. The selected processes are genuinely one thing -- one backend table, one document --
 * so rendering them twice would be the drift it is worth avoiding.
 *
 * THE RATIONALE IS WHY THE SECOND HALF EXISTS. "Why this process" is a design history question and
 * the first thing an auditor opens; the service refuses a blank one and the table refuses it
 * again, so every row here carries a reason. The pane therefore shows the reason as the row's
 * content rather than hiding it behind a chevron -- a design history file whose reasons are one
 * click away is one nobody reads.
 *
 * A REGULATORY CLASS IS SHOWN WITH ITS JURISDICTION OR NOT AT ALL. "Class II" is a different
 * device under the FDA, the EU MDR and the IVDR, so a class on its own is unreadable -- and worse
 * than a blank, because it LOOKS like a recorded fact. The write refuses that combination, and
 * where an older row has it the pane says the claim is incomplete rather than printing half of it
 * as though it meant something.
 *
 * AND "NOBODY HAS FILLED THIS IN" IS NOT THE SAME AS "THERE IS NOTHING HERE". `FieldsRecorded`
 * separates them, because a payload of nulls reads identically either way and only one of them is
 * somebody's job.
 *
 * READ-ONLY FOR NOW, SAID PLAINLY. Editing a regulatory claim is a design change that belongs in a
 * form with a revision behind it, and choosing a process needs a dialog that asks for the reason
 * rather than a field somebody can tab past.
 */
import React, {useCallback, useContext, useEffect, useState} from "react";
import {Box, Chip, CircularProgress, Tooltip, Typography} from "@mui/material";

import {AppContext} from "../../util/AppContext";
import {makeCresAxios} from "../../util/Axios";
import {Grey2, Grey7, Grey8, Grey17} from "../../theme/styles";

type Process = {
    USID: string; ProcessName?: string | null; Rationale?: string | null;
    AlternativesConsidered?: string | null; Sequence?: number | null;
    SubjectType?: string | null; LinkedToAProcessStep?: boolean;
};

type Detail = {
    ObjectID?: string | null; Name?: string | null;
    FieldsRecorded?: boolean; Count?: number;
    Experiment?: Record<string, any> | null;
    ListOfSelectedProcess?: {SelectedProcess?: Process[] | Process};
};

/** Which fields each species shows, in the order somebody reads them off the page. */
const FIELDS: Record<string, [string, string][]> = {
    Experiment: [
        ["ProcessType", "Process type"],
        ["BatchNumber", "Batch number"],
        ["PerformedDate", "Performed"],
        ["Outcome", "Outcome"],
    ],
};

function listOf<T>(held: T[] | T | undefined | null): T[] {
    if (held === null || held === undefined) return [];
    return Array.isArray(held) ? held : [held];
}

function Structured({label, value, note}: {label: string; value: any; note: string}) {
    const entries = value && typeof value === "object" && !Array.isArray(value)
        ? Object.entries(value) : [];
    return <Box sx={{paddingTop: "14px"}}>
        <Tooltip title={note}>
            <Typography sx={{fontSize: 11.5, color: Grey7, textTransform: "uppercase",
                             letterSpacing: "0.04em"}}>{label}</Typography>
        </Tooltip>
        {entries.length === 0 && (
            <Typography sx={{fontSize: 13, color: Grey7}}>— nothing recorded</Typography>
        )}
        {entries.length > 0 && (
            <Box sx={{display: "flex", gap: "22px", flexWrap: "wrap", paddingTop: "4px"}}>
                {entries.map(([key, held]) => (
                    <Box key={key}>
                        <Typography sx={{fontSize: 15, color: Grey17}}>{String(held)}</Typography>
                        <Typography sx={{fontSize: 12, color: Grey7}}>{key}</Typography>
                    </Box>
                ))}
            </Box>
        )}
    </Box>;
}

/** The experiment's fields and chosen processes. A PRODUCT HAD THIS PANE TOO until 2026-10-29,
 *  when a product became an Item of type PRT- whose fields are its Form tab; the species prop went
 *  with it. */
export default function DevelopmentPane({objectUSID}: {objectUSID?: string | null}) {
    const species = "Experiment" as const;

    const appContext: any = useContext(AppContext);
    const api = makeCresAxios(appContext?.sessionContext);

    const [detail, setDetail] = useState<Detail | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(() => {
        if (!objectUSID) return;
        setLoading(true);
        const path = "t1/v1/experiment_details";
        const key = "ExperimentObjectUSID";
        api.get(path, {params: {[key]: objectUSID}})
            .then((answer: any) => {
                setDetail(answer?.data?.PlatformMessage ?? null);
                setError(null);
            })
            .catch((problem: any) => {
                setDetail(null);
                setError(problem?.response?.data?.Cause
                         ?? `This ${species.toLowerCase()}'s details could not be read.`);
            })
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [objectUSID, species]);

    useEffect(() => { load(); }, [load]);

    if (loading && !detail && !error) {
        return <Box sx={{display: "flex", gap: "10px", alignItems: "center", padding: "20px 0"}}>
            <CircularProgress size={18}/>
            <Typography sx={{fontSize: 13, color: Grey7}}>Reading the record…</Typography>
        </Box>;
    }

    if (error) {
        return <Typography sx={{fontSize: 13, color: "#B3261E", lineHeight: 1.6, maxWidth: 760,
                                paddingTop: "8px"}}>{error}</Typography>;
    }

    const fields = detail?.Experiment ?? null;
    const processes = listOf<Process>(detail?.ListOfSelectedProcess?.SelectedProcess);
    const recorded = !!detail?.FieldsRecorded;

    return <Box sx={{paddingTop: "8px", maxWidth: 1100}}>

        {/* ── THE RECORD'S OWN FIELDS ───────────────────────────────────────────────────── */}
        {!recorded && (
            <Typography sx={{fontSize: 13, color: Grey7, lineHeight: 1.6, maxWidth: 820}}>
                Nobody has recorded what this {species.toLowerCase()}
                {" was run with or what it found yet."}
                {" "}That is not the same as there being nothing to record.
            </Typography>
        )}

        {recorded && fields && (
            <Box>
                <Box sx={{display: "flex", gap: "32px", flexWrap: "wrap"}}>
                    {FIELDS[species].map(([key, label]) => (
                        <Box key={key} sx={{maxWidth: 360}}>
                            <Typography sx={{fontSize: 11.5, color: Grey7,
                                             textTransform: "uppercase",
                                             letterSpacing: "0.04em"}}>{label}</Typography>
                            <Typography sx={{fontSize: 13, color: Grey17, lineHeight: 1.5}}>
                                {fields[key] || "—"}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                {(
                    <>
                        <Structured label="Parameters" value={fields.Parameters}
                                    note="What was done. Stored as named values because the point
                                          of recording parameters is to correlate them with
                                          outcomes across runs."/>
                        <Structured label="Results" value={fields.Results}
                                    note="What was measured. The OUTCOME above is what somebody
                                          decided it meant, which is a different thing."/>
                        {fields.HasResults && !fields.Concluded && (
                            <Typography sx={{fontSize: 12.5, color: "#8A6100", lineHeight: 1.6,
                                             paddingTop: "10px", maxWidth: 820}}>
                                Measured, but nobody has written down what it means. A result is a
                                number; the conclusion drawn from it is the thing a reviewer signs.
                            </Typography>
                        )}
                    </>
                )}
            </Box>
        )}

        {/* ── THE DESIGN DECISIONS ──────────────────────────────────────────────────────── */}
        <Typography sx={{fontSize: 15, color: Grey17, paddingTop: "26px"}}>
            Processes chosen, and why
        </Typography>

        {processes.length === 0 && (
            <Typography sx={{fontSize: 13, color: Grey7, lineHeight: 1.6, maxWidth: 820,
                             paddingTop: "6px"}}>
                No process has been chosen for this {species.toLowerCase()} yet. Each choice
                carries the reason behind it — “why this process” is a design history question, and
                a selection with no reason cannot be recorded here at all.
            </Typography>
        )}

        {processes.length > 0 && (
            <Box sx={{paddingTop: "8px"}}>
                {processes.map(one => (
                    <Box key={one.USID}
                         sx={{padding: "12px 0", borderBottom: `1px solid ${Grey2}`}}>
                        <Box sx={{display: "flex", gap: "10px", alignItems: "baseline"}}>
                            <Typography sx={{fontSize: 14, color: Grey17}}>
                                {one.ProcessName}
                            </Typography>
                            {!one.LinkedToAProcessStep && (
                                <Tooltip title="Chosen by name. A development process is often
                                                chosen before anybody has written it up as a
                                                routing step, which is ordinary.">
                                    <Chip label="not a routing step" size="small"
                                          variant="outlined"
                                          sx={{fontSize: 10.5, height: 20, color: Grey7}}/>
                                </Tooltip>
                            )}
                        </Box>
                        {/* THE REASON IS THE ROW'S CONTENT, not something behind a chevron. */}
                        <Typography sx={{fontSize: 13, color: Grey8, lineHeight: 1.6,
                                         paddingTop: "4px", maxWidth: 860}}>
                            {one.Rationale}
                        </Typography>
                        {one.AlternativesConsidered && (
                            <Typography sx={{fontSize: 12.5, color: Grey7, lineHeight: 1.6,
                                             paddingTop: "4px", maxWidth: 860}}>
                                Also considered: {one.AlternativesConsidered}
                            </Typography>
                        )}
                    </Box>
                ))}
            </Box>
        )}

        <Typography sx={{fontSize: 12.5, color: Grey7, lineHeight: 1.6, maxWidth: 820,
                         paddingTop: "16px"}}>
            This panel is read-only. Editing a regulatory claim is a design change that belongs in
            a form with a revision behind it, and choosing a process needs a dialog that asks for
            the reason rather than a field somebody can tab past.
        </Typography>
    </Box>;
}
